import React, { useState } from 'react';
import * as Icons from '@seismic/icons';
import ScrollBar from 'react-custom-scrollbars';
import { useTranslation } from 'react-i18next';
import '../style/style.scss';
import DocumentCard from './documentCard';
import UxEventService from '../service/UxEventService';
import { UXContextType, isMobileDevice, isShowSignature, isNoTrackView } from '../commonUtil/util';
import { isLargeDesktop } from '../commonUtil/responsiveUtil';
import { withLinkStore, ACTION_TYPES_SHOW_SHARE_MODAL, ACTION_TYPES_UPDATE_DEFAULT_PRIVACY_URL } from '../app/reducers';
import UserAvatar from './common/viewerAvatar';
import { ShareIcon } from './common/icons';
import { isPresentationMode } from '../commonUtil/presentationUtil';
import AttendeesList from './attendeesList';
import MyPrivacySettings from './myPrivacySettings';
import PrivacySettingButton from './common/privacySettingButton';
import { isExplicitGDPR, isGDPROn } from '../commonUtil/gdprUtil';
import { KeyBoardAction } from '../commonUtil/constants';
import DumpPrivacyLink from '../dumpComponent/dumpPrivacyLink';

const ViewerPanel = (props) => {
    const { t } = useTranslation();
    const isShow4Signature = isShowSignature();
    const { sender, viewerSetting, gdprSetting, contents, linkBasicInfo, creatingSession, isOnlyFunctionalCookies, viewerLocation } = props.linkStore;
    const [onHover, setOnHover] = useState(false);
    const [isOpenPrivacySetting, setIsOpenPrivacySetting] = useState(false);

    const { logo, brandingHilightColor } = viewerSetting;
    const { isAllowSharingLink, isExpired, interactionId } = linkBasicInfo;

    const OnSelectContent = (content) => {
        if (isMobileDevice.any()) {
            props.setOpenPanel(false);
        }

        props.onSelectContent(content);

        UxEventService.navigateViewEvent({ Context: UXContextType.VIEWER_PANEL, Target: 'Close' });
        UxEventService.contentOpenEvent({ Context: UXContextType.VIEWER_PANEL, Target: `Open ${content.format}` });
    };

    const togglePanel = () => {
        if (!props.isOpen && !onHover) {
            setOnHover(true);
        }

        props.setOpenPanel(!props.isOpen);
        UxEventService.navigateViewEvent({ Context: UXContextType.VIEWER_PANEL, Target: `${!props.isOpen ? 'Open' : 'Close'}` });
    };

    const openMyPrivacySettings = () => {
        setIsOpenPrivacySetting(true);
    };

    const handleMouseEnter = () => {
        if (props.isOpen && !onHover) {
            setOnHover(true);
        }
    };

    const handleMouseLeave = () => {
        if (props.isOpen && onHover) {
            setOnHover(false);
        }
    };

    const handleClickSendEmail = () => {
        UxEventService.navigateViewEvent({ Context: UXContextType.VIEWER_PANEL, Target: 'Send Email' });
    };

    const handleClickPrivacyLink = () => {
        UxEventService.navigateViewEvent({ Context: UXContextType.VIEWER_PANEL, Target: 'Privacy Link' });
    };

    const renderTrackVertical = (style, scrollbarProps) => {
        const scrollStyle = {
            ...style,
            width: '8px',
            opacity: onHover ? 1 : 0
        };

        return <div {...scrollbarProps} className="livesend-viewer-vertical-scrollbar" style={scrollStyle} />;
    };

    const handleOpenPanel = () => {
        if (props.isOpen) {
            return;
        }

        props.setOpenPanel(true);
    };

    const handleOnKeydown = (e) => {
        if (e.keyCode === KeyBoardAction.enter) {
            togglePanel();
        }
    };

    const shareDesc = () => {
        if (isLargeDesktop()) {
            if (contents.length === 1) {
                return t('LIVE_SEND_VIEWER_SHARED_FILE', { replace: { senderName: sender.name } });
            }

            return t('LIVE_SEND_VIEWER_SHARED_FILES',
                { replace: { senderName: sender.name, contentCount: contents.length } });
        }

        if (contents.length === 1) {
            return t('LIVE_SEND_VIEWER_SHARED_FILE_WITH_YOU',
                { replace: { senderName: sender.name, contentCount: contents.length } });
        }

        return t('LIVE_SEND_VIEWER_SHARED_FILES_WITH_YOU',
            { replace: { senderName: sender.name, contentCount: contents.length } });
    };

    const openShareModal = () => {
        props.linkDispatch({ type: ACTION_TYPES_SHOW_SHARE_MODAL, payload: true });
        UxEventService.navigateViewEvent({ Context: UXContextType.HEADER, Target: 'Show Share Modal' });
    };

    const updateSelectPrivacyLink = (result) => {
        props.linkDispatch({ type: ACTION_TYPES_UPDATE_DEFAULT_PRIVACY_URL, payload: result });
    };

    const showShareButton = interactionId && isAllowSharingLink && !creatingSession && !isExpired && !isNoTrackView() && !isOnlyFunctionalCookies;
    return (
        <div role="region" aria-label="Content List Panel" className={`livesend-viewer-panel-container ${props.isOpen ? 'open' : ''}`}>
            {
                !isMobileDevice.any() && !props.isExpiredLink &&
                <div key="TogglePanelBtn"
                    id="togglePanelBtn"
                    className="toggle-panel-btn"
                    onClick={togglePanel}
                    onKeyDown={handleOnKeydown}
                    aria-label="toggle panel button"
                    aria-expanded={String(props.isOpen)}
                    aria-controls="livesendViewerPanel"
                    tabIndex="0"
                    role="button">
                    <Icons.CaretLeftIcon />
                </div>
            }
            <div className="livesend-viewer-panel-content"
                onClick={handleOpenPanel}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}>
                <ScrollBar renderTrackVertical={renderTrackVertical} className="scroll-box">
                    {
                        (isLargeDesktop() || !isMobileDevice.any()) && !props.isOpen &&
                        <div className={`toggle-panel-menu-bar ${isShow4Signature ? '' : 'signature'}`} onClick={togglePanel}>
                            <svg width="22" height="18" viewBox="0 0 22 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1.67773 4.02779C2.60432 4.02779 3.35547 3.27381 3.35547 2.34373C3.35547 1.41365 2.60432 0.659668 1.67773 0.659668C0.751147 0.659668 0 1.41365 0 2.34373C0 3.27381 0.751147 4.02779 1.67773 4.02779Z" fill="#92A2AB" />
                                <path d="M19.5904 1.22119H8.0052C7.22608 1.22119 6.59375 1.72416 6.59375 2.34389C6.59375 2.96362 7.22608 3.46658 8.0052 3.46658H19.5904C20.3709 3.46658 21.0018 2.96362 21.0018 2.34389C21.0018 1.72416 20.3695 1.22119 19.5904 1.22119Z" fill="#92A2AB" />
                                <path d="M1.67773 10.5292C2.60432 10.5292 3.35547 9.77525 3.35547 8.84518C3.35547 7.9151 2.60432 7.16113 1.67773 7.16113C0.751147 7.16113 0 7.9151 0 8.84518C0 9.77525 0.751147 10.5292 1.67773 10.5292Z" fill="#92A2AB" />
                                <path d="M19.5904 7.72168H8.0052C7.22608 7.72168 6.59375 8.22465 6.59375 8.84438C6.59375 9.4641 7.22608 9.96707 8.0052 9.96707H19.5904C20.3709 9.96707 21.0018 9.4641 21.0018 8.84438C21.0018 8.22465 20.3695 7.72168 19.5904 7.72168Z" fill="#92A2AB" />
                                <path d="M1.67773 17.0292C2.60432 17.0292 3.35547 16.2752 3.35547 15.3452C3.35547 14.4151 2.60432 13.6611 1.67773 13.6611C0.751147 13.6611 0 14.4151 0 15.3452C0 16.2752 0.751147 17.0292 1.67773 17.0292Z" fill="#92A2AB" />
                                <path d="M19.5904 14.2231H8.0052C7.22608 14.2231 6.59375 14.7261 6.59375 15.3458C6.59375 15.9656 7.22608 16.4685 8.0052 16.4685H19.5904C20.3709 16.4685 21.0018 15.9656 21.0018 15.3458C21.0018 14.7261 20.3695 14.2231 19.5904 14.2231Z" fill="#92A2AB" />
                            </svg>
                        </div>
                    }
                    <div id="livesendViewerPanel" className="livesend-viewer-panel" role="region" aria-labelledby="togglePanelBtn">
                        {
                            !isLargeDesktop() && isMobileDevice.any() && <div className={`mobile-panel-header ${showShareButton ? 'with-share' : ''}`}>
                                {logo && <div className="customer-logo"><img src={logo} alt="customer-logo" /></div>}
                                {showShareButton &&
                                <div className="mobile-share-btn"
                                    onClick={openShareModal}
                                    role="button"
                                    tabIndex="0"
                                    style={{ color: brandingHilightColor, borderColor: brandingHilightColor }}>
                                    <ShareIcon color={brandingHilightColor} />
                                    <span className="text">{t('LIVE_SEND_VIEWER_SHARE')}</span>
                                </div>}
                            </div>
                        }

                        { isShow4Signature && <div className="user-thumbnail">
                            {!sender.isAnonymousSender && <UserAvatar className="user-thumbnail_avatar" sender={sender} brandingHilightColor={brandingHilightColor} />}
                        </div>}
                        {
                            props.isOpen &&
                            [isPresentationMode() && <span key="presentingTitle" className="livesend-viewer-presenting-title">{sender.name} is presenting</span>,
                                <div className="livesend-documents" key="documentList">
                                    { isShow4Signature && <span className="livesend-documents-desc" dangerouslySetInnerHTML={{ __html: shareDesc() }} />}
                                    { isShow4Signature && !sender.isAnonymousSender && <a onClick={handleClickSendEmail}
                                        style={{ color: brandingHilightColor }}
                                        href={`mailto:${sender.email}`}
                                        className="email-me-btn">
                                        {t('LIVE_SEND_VIEWER_CONTACT')}
                                    </a>}
                                    <div className={`document-list ${props.isExpiredLink ? 'disable' : ''}`}>
                                        <div role="listbox">
                                            {contents.map(content => <DocumentCard key={content.id} content={content} onClick={OnSelectContent} />)}
                                        </div>
                                        {
                                            isPresentationMode() && <AttendeesList key="presentingList" brandingHilightColor={brandingHilightColor} />
                                        }
                                    </div>
                                </div>
                            ]
                        }
                        {!isPresentationMode() && !props.isExpiredLink && <div className="viewer-panel-privacy">
                            {
                                gdprSetting.privacyLink &&
                                    <DumpPrivacyLink
                                        updateSelectLink={updateSelectPrivacyLink}
                                        clickHandler={handleClickPrivacyLink}
                                        privacyLinkUrl={gdprSetting.privacyLink}
                                        privacyPolicyText={t('LIVE_SEND_PRIVACY_POLICY')}
                                        hilightColor="#293f4d" />
                            }
                            {
                                !isExpired &&
                                !isExplicitGDPR(gdprSetting.consentLocation) &&
                                isGDPROn(gdprSetting, viewerLocation) &&
                                gdprSetting.isShowPrivacySettingBtn &&
                                <PrivacySettingButton
                                    useHyperLink
                                    translate={t}
                                    clickHandler={openMyPrivacySettings} />
                            }
                        </div>}
                    </div>
                </ScrollBar>
            </div>
            <MyPrivacySettings
                showSave
                isOpen={isOpenPrivacySetting}
                setIsOpen={setIsOpenPrivacySetting}
                isShowStrictlyNecessaryCookies={gdprSetting.isShowRejectAllBtn} />
        </div>
    );
};

export default withLinkStore(ViewerPanel);
